import { useMemo, useState } from 'react';
import { useRelationships } from '@/api/queries';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { Modal } from '@/components/ui/Modal';
import { imageUrl } from '@/api/client';

interface OtherNode {
  id: number;
  x: number;
  y: number;
  name: string;
  portrait: string | null;
  value: number;
}

function getRelColor(value: number): string {
  if (value < 0) return '#c81e3a'; // багровый
  const t = Math.min(1, value / 100);
  const lerp = (a: number, b: number) => Math.round(a + (b - a) * t);
  // gray #9ca3af (0) -> pink #f472b6 (100)
  return `rgb(${lerp(0x9c, 0xf4)},${lerp(0xa3, 0x72)},${lerp(0xaf, 0xb6)})`;
}

export function RelationshipsOverlay() {
  const userId = useAuthStore((s) => s.userId);
  const centerId = useUIStore((s) => s.selectedWaifuId);
  const open = useUIStore((s) => s.isModalOpen('relationships'));
  const closeModal = useUIStore((s) => s.closeModal);
  const { data } = useRelationships(userId);

  const [hoveredOtherId, setHoveredOtherId] = useState<number | null>(null);

  const center = useMemo(() => {
    if (!data || centerId == null) return null;
    const w = data.waifus[centerId];
    return w ? { ...w, x: 200, y: 200 } : null;
  }, [data, centerId]);

  const others = useMemo<OtherNode[]>(() => {
    if (!data || centerId == null) return [];
    // Map center -> other waifu relationship values.
    const valueByOther = new Map<number, number>();
    data.relationships.forEach((rel) => {
      if (
        rel.entity1_type === 'waifu' &&
        rel.entity1_id === centerId &&
        rel.entity2_type === 'waifu' &&
        rel.entity2_id != null
      ) {
        valueByOther.set(rel.entity2_id, rel.value);
      }
    });

    const list = Object.values(data.waifus).filter((w) => w.id !== centerId);
    const radius = 140;
    return list.map((w, i) => {
      const angle = (i / Math.max(1, list.length)) * Math.PI * 2 - Math.PI / 2;
      return {
        id: w.id,
        x: 200 + Math.cos(angle) * radius,
        y: 200 + Math.sin(angle) * radius,
        name: w.name,
        portrait: w.portrait_url,
        value: valueByOther.get(w.id) ?? 0,
      };
    });
  }, [data, centerId]);

  return (
    <Modal
      open={open}
      onClose={() => closeModal('relationships')}
      title={center ? `Отношения · ${center.name}` : 'Отношения'}
      size="lg"
    >
      <div className="p-4">
        {!data || !center ? (
          <div className="text-white/40 text-sm py-8 text-center">Загружаем…</div>
        ) : others.length === 0 ? (
          <div className="text-white/40 text-sm py-8 text-center">
            Нет других вайфу для отображения связей.
          </div>
        ) : (
          <svg viewBox="0 0 400 400" className="w-full">
            {/* Lines from center to each other waifu */}
            {others.map((o) => {
              const color = getRelColor(o.value);
              const hovered = hoveredOtherId === o.id;
              return (
                <g key={`line-${o.id}`}>
                  <line
                    x1={center.x}
                    y1={center.y}
                    x2={o.x}
                    y2={o.y}
                    stroke={color}
                    strokeOpacity={hovered ? 1 : 0.85}
                    strokeWidth={3}
                  />
                  {/* Transparent wide hit area for hover */}
                  <line
                    x1={center.x}
                    y1={center.y}
                    x2={o.x}
                    y2={o.y}
                    stroke="transparent"
                    strokeWidth={14}
                    style={{ cursor: 'pointer', pointerEvents: 'stroke' }}
                    onMouseEnter={() => setHoveredOtherId(o.id)}
                    onMouseLeave={() => setHoveredOtherId(null)}
                    onTouchStart={() => setHoveredOtherId(o.id)}
                    onTouchEnd={() => setHoveredOtherId(null)}
                  />
                  {hovered && (
                    <text
                      x={(center.x + o.x) / 2}
                      y={(center.y + o.y) / 2 - 4}
                      textAnchor="middle"
                      fill="#ffffff"
                      stroke="#0b0b14"
                      strokeWidth={3}
                      paintOrder="stroke"
                      strokeLinejoin="round"
                      fontSize={12}
                      fontWeight="700"
                      style={{ pointerEvents: 'none' }}
                    >
                      {o.value}
                    </text>
                  )}
                </g>
              );
            })}

            {/* Other waifu nodes */}
            {others.map((o) => (
              <g key={`node-${o.id}`}>
                <circle
                  cx={o.x}
                  cy={o.y}
                  r={24}
                  fill="#15152a"
                  stroke="#a78bfa"
                  strokeWidth={2}
                />
                {o.portrait && (
                  <image
                    href={imageUrl(o.portrait)}
                    x={o.x - 22}
                    y={o.y - 22}
                    width={44}
                    height={44}
                    clipPath="circle(22px at center)"
                  />
                )}
                <text
                  x={o.x}
                  y={o.y + 40}
                  textAnchor="middle"
                  fill="white"
                  fontSize={10}
                >
                  {o.name}
                </text>
              </g>
            ))}

            {/* Center waifu node */}
            <g>
              <circle
                cx={center.x}
                cy={center.y}
                r={30}
                fill="#15152a"
                stroke="#fbbf24"
                strokeWidth={3}
              />
              {center.portrait_url && (
                <image
                  href={imageUrl(center.portrait_url)}
                  x={center.x - 28}
                  y={center.y - 28}
                  width={56}
                  height={56}
                  clipPath="circle(28px at center)"
                />
              )}
              <text
                x={center.x}
                y={center.y + 46}
                textAnchor="middle"
                fill="#fbbf24"
                fontSize={11}
                fontWeight="600"
              >
                {center.name}
              </text>
            </g>
          </svg>
        )}
      </div>
    </Modal>
  );
}
